import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { User, AuthState, LoginCredentials, RegisterData } from '../types'
import { FraudDetection } from '../utils/fraudDetection'

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>
  register: (data: RegisterData, referralCode?: string) => Promise<boolean>
  logout: () => void
  verifyAge: () => void
  updateUser: (user: User) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AGE_VERIFIED'; payload: boolean }
  | { type: 'LOGOUT' }

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isLoggedIn: !!action.payload,
        isLoading: false
      }
    case 'SET_AGE_VERIFIED':
      return { ...state, isAgeVerified: action.payload }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        isLoading: false
      }
    default:
      return state
  }
}

const initialState: AuthState = {
  user: null,
  isLoggedIn: false,
  isAgeVerified: false,
  isLoading: true
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check authentication with backend on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include'
        })
        
        if (response.ok) {
          const data = await response.json()
          console.log('Auth check successful - User:', data.user.username, 'Role:', data.user.role)
          dispatch({ type: 'SET_USER', payload: data.user })
        } else {
          console.log('Auth check failed - Status:', response.status)
          // Explicitly set user to null if not authenticated
          dispatch({ type: 'SET_USER', payload: null })
        }
      } catch (error) {
        console.error('Error checking auth:', error)
        // Set user to null on error to ensure unauthenticated state
        dispatch({ type: 'SET_USER', payload: null })
      }
      
      const savedAgeVerified = localStorage.getItem('unbiased-kratom-age-verified')
      if (savedAgeVerified === 'true') {
        dispatch({ type: 'SET_AGE_VERIFIED', payload: true })
      }
      
      // Only set loading to false after auth check is complete
      dispatch({ type: 'SET_LOADING', payload: false })
    }
    
    checkAuth()
  }, [])

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          login: credentials.username,
          password: credentials.password
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        dispatch({ type: 'SET_USER', payload: data.user })
        return true
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
        return false
      }
    } catch (error) {
      console.error('Login error:', error)
      dispatch({ type: 'SET_LOADING', payload: false })
      return false
    }
  }

  const register = async (data: RegisterData, referralCode?: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      // Add fraud detection data
      const enrichedData: RegisterData = {
        ...data,
        ipAddress: await FraudDetection.getUserIP(),
        userAgent: navigator.userAgent,
        registrationTimestamp: new Date(),
        referralCode: referralCode || data.referralCode
      }
      
      // Perform fraud check
      const fraudCheck = FraudDetection.performFraudCheck(enrichedData)
      
      // Block high-risk registrations
      if (fraudCheck.status === 'blocked') {
        console.log('Registration blocked due to fraud risk:', fraudCheck.riskFactors)
        dispatch({ type: 'SET_LOADING', payload: false })
        return false
      }
      
      // Validate age (must be 21+)
      const birthDate = new Date(data.dateOfBirth)
      const age = new Date().getFullYear() - birthDate.getFullYear()
      if (age < 21) {
        dispatch({ type: 'SET_LOADING', payload: false })
        return false
      }
      
      // Check if username already exists
      const savedUsers = JSON.parse(localStorage.getItem('unbiased-kratom-users') || '[]')
      if (savedUsers.some((u: any) => u.username === data.username || u.email === data.email)) {
        dispatch({ type: 'SET_LOADING', payload: false })
        return false
      }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const newUser: User = {
        id: Date.now().toString(),
        username: data.username,
        email: data.email,
        dateOfBirth: birthDate,
        isAgeVerified: data.ageVerification,
        dateCreated: new Date(),
        isVerified: false,
        role: 'member'
      }
      
      // Save user to localStorage (in real app, this would be API call)
      const userWithPassword = { ...newUser, password: data.password }
      savedUsers.push(userWithPassword)
      localStorage.setItem('unbiased-kratom-users', JSON.stringify(savedUsers))
      
      // Update fraud check with real user ID
      fraudCheck.userId = newUser.id
      const fraudChecks = FraudDetection.getFraudChecks()
      const lastCheck = fraudChecks[fraudChecks.length - 1]
      if (lastCheck) {
        lastCheck.userId = newUser.id
        localStorage.setItem('fraudChecks', JSON.stringify(fraudChecks))
      }
      
      // Handle referral tracking (only for approved users)
      if (referralCode && fraudCheck.status === 'approved') {
        handleReferralSignup(newUser.id, referralCode)
      }
      
      // Create referral code for new user
      createReferralCode(newUser.id, newUser.username)
      
      // Give signup bonus (only for approved users)
      if (fraudCheck.status === 'approved') {
        giveSignupBonus(newUser.id)
      }
      
      // Set current user
      dispatch({ type: 'SET_USER', payload: newUser })
      localStorage.setItem('unbiased-kratom-user', JSON.stringify(newUser))
      
      // Set age verification if confirmed
      if (data.ageVerification) {
        dispatch({ type: 'SET_AGE_VERIFIED', payload: true })
        localStorage.setItem('unbiased-kratom-age-verified', 'true')
      }
      
      return true
    } catch (error) {
      console.error('Registration error:', error)
      dispatch({ type: 'SET_LOADING', payload: false })
      return false
    }
  }

  const handleReferralSignup = (newUserId: string, referralCode: string) => {
    // Find the referrer by their referral code
    const referralCodes = JSON.parse(localStorage.getItem('referralCodes') || '[]')
    const code = referralCodes.find((rc: any) => rc.code === referralCode && rc.isActive)
    
    if (code) {
      // Create referral record
      const referrals = JSON.parse(localStorage.getItem('referrals') || '[]')
      const newReferral = {
        id: Date.now().toString(),
        referrerId: code.userId,
        refereeId: newUserId,
        referralCode: referralCode,
        status: 'pending',
        dateReferred: new Date(),
        rewardAwarded: false
      }
      referrals.push(newReferral)
      localStorage.setItem('referrals', JSON.stringify(referrals))
      
      // Update referral code usage
      code.currentUsage += 1
      localStorage.setItem('referralCodes', JSON.stringify(referralCodes))
    }
  }

  const createReferralCode = (userId: string, username: string) => {
    const referralCodes = JSON.parse(localStorage.getItem('referralCodes') || '[]')
    const newCode = {
      id: Date.now().toString(),
      userId: userId,
      code: `KRATOM${username.toUpperCase()}`,
      isActive: true,
      createdDate: new Date(),
      currentUsage: 0
    }
    referralCodes.push(newCode)
    localStorage.setItem('referralCodes', JSON.stringify(referralCodes))
  }

  const giveSignupBonus = (userId: string) => {
    const transactions = JSON.parse(localStorage.getItem('rewardTransactions') || '[]')
    const signupBonus = {
      id: Date.now().toString(),
      userId: userId,
      type: 'signup_bonus',
      amount: 500,
      description: 'Welcome bonus for joining',
      date: new Date(),
      status: 'completed'
    }
    transactions.push(signupBonus)
    localStorage.setItem('rewardTransactions', JSON.stringify(transactions))
    
    // Update user rewards
    const userRewards = JSON.parse(localStorage.getItem('userRewards') || '[]')
    const existingReward = userRewards.find((ur: any) => ur.userId === userId)
    if (existingReward) {
      existingReward.totalEarned += 500
      existingReward.currentBalance += 500
    } else {
      const newReward = {
        userId: userId,
        totalEarned: 500,
        currentBalance: 500,
        totalRedeemed: 0,
        referralCount: 0,
        tier: 'bronze',
        tierProgress: 0,
        nextTierRequirement: 5
      }
      userRewards.push(newReward)
    }
    localStorage.setItem('userRewards', JSON.stringify(userRewards))
  }

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout error:', error)
    }
    dispatch({ type: 'LOGOUT' })
  }

  const verifyAge = () => {
    dispatch({ type: 'SET_AGE_VERIFIED', payload: true })
    localStorage.setItem('unbiased-kratom-age-verified', 'true')
  }

  const updateUser = (user: User) => {
    dispatch({ type: 'SET_USER', payload: user })
    localStorage.setItem('unbiased-kratom-user', JSON.stringify(user))
  }

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    verifyAge,
    updateUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}